/**
 * Loader for the precomputed "Browse by Trade" constellation graph
 * (public/ofo/explore-graph.json — gzipped, built by @rosta/ofo-codes compiler).
 *
 * The compiler does all heavy lifting (typed weighted edges, per-occupation
 * adjacency, PCA seed layout). The client just fetches, caches
 * (IndexedDB, versioned by createdAt) and renders. Mirrors the caching pattern
 * in ofo-client-search.ts.
 */

const GRAPH_URL = '/ofo/explore-graph.json';
const DB_NAME = 'bp-ofo-explore';
const STORE = 'kv';

export type ExploreNodeLevel =
  | 'occupation'
  | 'unitGroup'
  | 'minorGroup'
  | 'subMajorGroup'
  | 'majorGroup';

export type ExploreEdgeType = 'PART_OF' | 'SIMILAR_TO' | 'RELATED_TO';

export type GroupTier = 'majorGroup' | 'subMajorGroup' | 'minorGroup' | 'unitGroup';

export interface ExploreNode {
  id: string;
  code: string;
  level: ExploreNodeLevel;
  title: string;
  major: string;
  skill: string;
  trade: boolean;
  green: boolean;
  count: number;
  x: number;
  y: number;
  parent: string | null;
  r: number;
}

export interface ExploreEdge {
  s: string;
  t: string;
  k: ExploreEdgeType;
  w: number;
}

export interface ExploreGraph {
  version: number;
  createdAt: string;
  nodes: ExploreNode[];
  edges: ExploreEdge[];
  adjacency: Record<string, Array<[string, number]>>;
  groupEdges: Record<GroupTier, ExploreEdge[]>;
}

// ── IndexedDB cache ─────────────────────────────────────────────────────────
function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbGet<T>(key: string): Promise<T | null> {
  try {
    const db = await openDB();
    return await new Promise<T | null>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).get(key);
      req.onsuccess = () => resolve((req.result as T) ?? null);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return null;
  }
}

async function idbPut(key: string, val: unknown): Promise<void> {
  try {
    const db = await openDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(val, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    /* best-effort */
  }
}

async function gunzip(buf: ArrayBuffer): Promise<ArrayBuffer> {
  const ds = new DecompressionStream('gzip');
  const stream = new Response(buf).body!.pipeThrough(ds);
  return await new Response(stream).arrayBuffer();
}

// ── Derived, indexed view for fast lookup during rendering ──────────────────
export interface ExploreIndex {
  graph: ExploreGraph;
  byId: Map<string, ExploreNode>;
  byCode: Map<string, ExploreNode>;
  occupations: ExploreNode[];
  /** node id → direct child node ids (one tier down). */
  children: Map<string, string[]>;
  /** the 8 major-group super-nodes (top of the hierarchy). */
  majors: ExploreNode[];
}

const TIER_LEVEL: Record<GroupTier, ExploreNodeLevel> = {
  majorGroup: 'majorGroup',
  subMajorGroup: 'subMajorGroup',
  minorGroup: 'minorGroup',
  unitGroup: 'unitGroup',
};

function indexGraph(graph: ExploreGraph): ExploreIndex {
  const byId = new Map<string, ExploreNode>();
  const byCode = new Map<string, ExploreNode>();
  const children = new Map<string, string[]>();
  for (const n of graph.nodes) {
    byId.set(n.id, n);
    byCode.set(n.code, n);
  }
  for (const n of graph.nodes) {
    if (n.parent) {
      const arr = children.get(n.parent) ?? [];
      arr.push(n.id);
      children.set(n.parent, arr);
    }
  }
  const occupations = graph.nodes.filter((n) => n.level === 'occupation');
  const majors = graph.nodes
    .filter((n) => n.level === 'majorGroup')
    .sort((a, b) => a.major.localeCompare(b.major));
  return { graph, byId, byCode, occupations, children, majors };
}

/** Direct children (one tier down) of a group node, as ExploreNodes. */
export function childrenOf(index: ExploreIndex, id: string): ExploreNode[] {
  return (index.children.get(id) ?? [])
    .map((cid) => index.byId.get(cid)!)
    .filter(Boolean);
}

/** All group nodes at a given tier (e.g. the 125 minor groups). */
export function groupsAtTier(index: ExploreIndex, tier: GroupTier): ExploreNode[] {
  const level = TIER_LEVEL[tier];
  return index.graph.nodes.filter((n) => n.level === level);
}

/**
 * Flatten a group node to its occupation descendants, walking every tier in
 * between (e.g. minorGroup → unitGroup → occupation).
 */
export function occupationsUnder(index: ExploreIndex, groupId: string): ExploreNode[] {
  const out: ExploreNode[] = [];
  const walk = (id: string) => {
    for (const child of childrenOf(index, id)) {
      if (child.level === 'occupation') out.push(child);
      else walk(child.id);
    }
  };
  walk(groupId);
  return out;
}

/** Walk a node's parent chain up to the enclosing group node at `tier`. */
export function ancestorAtTier(
  index: ExploreIndex,
  code: string,
  tier: GroupTier,
): string | null {
  const level = TIER_LEVEL[tier];
  let n: ExploreNode | undefined = index.byCode.get(code);
  while (n && n.level !== level) {
    n = n.parent ? index.byId.get(n.parent) : undefined;
  }
  return n?.id ?? null;
}

export { TIER_LEVEL };

let promise: Promise<ExploreIndex> | null = null;

async function load(): Promise<ExploreIndex> {
  let graph: ExploreGraph | null = null;
  try {
    const res = await fetch(GRAPH_URL, { cache: 'no-cache' });
    if (!res.ok) throw new Error(`explore-graph ${res.status}`);
    const buf = await gunzip(await res.arrayBuffer());
    graph = JSON.parse(new TextDecoder().decode(buf)) as ExploreGraph;
    idbPut('graph-latest', graph);
  } catch {
    graph = await idbGet<ExploreGraph>('graph-latest');
  }
  if (!graph) throw new Error('Explore graph unavailable (offline and not cached)');
  return indexGraph(graph);
}

/** Lazily load + cache the constellation graph (singleton). */
export function loadExploreGraph(): Promise<ExploreIndex> {
  if (!promise) promise = load();
  return promise;
}
